/**
 * MatchPairsTemplate - Dynamic Liquid Agentic UI
 *
 * Two columns of items to match. Click-to-select matching with
 * SVG connection lines, animated feedback for correct/incorrect pairs.
 *
 * Props:
 *   config     - { content: { questions: [{
 *                   pairs: [{ left: string, right: string }],
 *                   concept?: string
 *                 }] } }
 *   onAnswer   - (isCorrect, concept, responseTimeMs) => void
 *   onComplete - ({ score, correct, total, results, bestStreak }) => void
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  LinearProgress,
  Fade,
  Grow,
} from '@mui/material';
import { kidsColors, kidsAnimations } from '../kidsTheme';

const PAIR_COLORS = [
  kidsColors.blue,
  kidsColors.pink,
  kidsColors.orange,
  kidsColors.secondary,
  kidsColors.purple,
  kidsColors.yellow,
  kidsColors.correct,
  kidsColors.primaryLight,
];

// Fisher-Yates shuffle
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function MatchPairsTemplate({ config, onAnswer, onComplete }) {
  const questions = config?.content?.questions ?? [];
  const total = questions.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState([]);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  // Per-round state
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]); // [{leftIdx, rightIdx, color}]
  const [wrongFlash, setWrongFlash] = useState(null); // {leftIdx, rightIdx}
  const [visible, setVisible] = useState(true);
  const [roundScore, setRoundScore] = useState(0);

  const startTimeRef = useRef(Date.now());
  const containerRef = useRef(null);
  const leftRefs = useRef([]);
  const rightRefs = useRef([]);
  const [linePositions, setLinePositions] = useState([]);

  const q = questions[currentIndex] ?? { pairs: [] };
  const pairs = q.pairs ?? [];

  // Shuffle right column once per question
  const shuffledRightIndices = useMemo(
    () => shuffle(pairs.map((_, i) => i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentIndex, pairs.length],
  );

  useEffect(() => {
    startTimeRef.current = Date.now();
    setVisible(true);
    setSelectedLeft(null);
    setSelectedRight(null);
    setMatchedPairs([]);
    setWrongFlash(null);
    setRoundScore(0);
  }, [currentIndex]);

  // Recalculate SVG line positions when matches change
  useEffect(() => {
    const updateLines = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newPositions = matchedPairs.map((m) => {
        const leftEl = leftRefs.current[m.leftIdx];
        const rightEl = rightRefs.current[m.rightIdx];
        if (!leftEl || !rightEl) return null;
        const lr = leftEl.getBoundingClientRect();
        const rr = rightEl.getBoundingClientRect();
        return {
          x1: lr.right - containerRect.left,
          y1: lr.top + lr.height / 2 - containerRect.top,
          x2: rr.left - containerRect.left,
          y2: rr.top + rr.height / 2 - containerRect.top,
          color: m.color,
        };
      }).filter(Boolean);
      setLinePositions(newPositions);
    };
    updateLines();
    window.addEventListener('resize', updateLines);
    return () => window.removeEventListener('resize', updateLines);
  }, [matchedPairs]);

  // ── handle selection logic ───────────────────────────────────
  const tryMatch = useCallback(
    (leftIdx, rightIdx) => {
      const elapsed = Date.now() - startTimeRef.current;
      const leftItem = pairs[leftIdx]?.left;
      const rightItem = pairs[shuffledRightIndices[rightIdx]]?.right ?? '';
      // The correct right for leftIdx is pairs[leftIdx].right
      const correctRight = pairs[leftIdx]?.right;
      const isCorrect = rightItem === correctRight;

      const concept = q.concept ?? '';

      if (isCorrect) {
        const color = PAIR_COLORS[matchedPairs.length % PAIR_COLORS.length];
        const newMatched = [...matchedPairs, { leftIdx, rightIdx, color }];
        setMatchedPairs(newMatched);

        const newRoundScore = roundScore + 1;
        setRoundScore(newRoundScore);
        const newStreak = streak + 1;
        setStreak(newStreak);
        setBestStreak((b) => Math.max(b, newStreak));

        if (onAnswer) onAnswer(true, concept, elapsed);

        // Check if all pairs matched
        if (newMatched.length === pairs.length) {
          const newScore = score + newRoundScore;
          setScore(newScore);

          const result = {
            questionIndex: currentIndex,
            pairsMatched: newRoundScore,
            totalPairs: pairs.length,
            isCorrect: true,
            concept,
            responseTimeMs: elapsed,
          };
          const newResults = [...results, result];
          setResults(newResults);

          setTimeout(() => {
            setVisible(false);
            setTimeout(() => {
              if (currentIndex + 1 < total) {
                setCurrentIndex((i) => i + 1);
              } else {
                if (onComplete) {
                  onComplete({
                    score: newScore,
                    correct: newScore,
                    total: questions.reduce((sum, qq) => sum + (qq.pairs?.length ?? 0), 0),
                    results: newResults,
                    bestStreak: Math.max(bestStreak, newStreak),
                  });
                }
              }
            }, 250);
          }, 800);
        }
      } else {
        setStreak(0);
        setWrongFlash({ leftIdx, rightIdx });
        if (onAnswer) onAnswer(false, concept, elapsed);
        setTimeout(() => setWrongFlash(null), 700);
      }

      setSelectedLeft(null);
      setSelectedRight(null);
    },
    [pairs, shuffledRightIndices, matchedPairs, roundScore, streak, bestStreak, score, results, currentIndex, total, questions, q.concept, onAnswer, onComplete],
  );

  const handleLeftClick = useCallback(
    (idx) => {
      // Ignore if already matched
      if (matchedPairs.some((m) => m.leftIdx === idx)) return;
      setSelectedLeft(idx);
      if (selectedRight !== null) {
        tryMatch(idx, selectedRight);
      }
    },
    [matchedPairs, selectedRight, tryMatch],
  );

  const handleRightClick = useCallback(
    (idx) => {
      if (matchedPairs.some((m) => m.rightIdx === idx)) return;
      setSelectedRight(idx);
      if (selectedLeft !== null) {
        tryMatch(selectedLeft, idx);
      }
    },
    [matchedPairs, selectedLeft, tryMatch],
  );

  // ── guard ────────────────────────────────────────────────────
  if (!questions.length || !pairs.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color={kidsColors.textSecondary}>No matching pairs available.</Typography>
      </Box>
    );
  }

  const progress = (currentIndex / total) * 100;

  // ── item button sx helper ────────────────────────────────────
  const itemSx = (side, idx, originalPairIdx) => {
    const isLeft = side === 'left';
    const isSelected = isLeft ? selectedLeft === idx : selectedRight === idx;
    const matched = isLeft
      ? matchedPairs.find((m) => m.leftIdx === idx)
      : matchedPairs.find((m) => m.rightIdx === idx);
    const isWrong = wrongFlash && (isLeft ? wrongFlash.leftIdx === idx : wrongFlash.rightIdx === idx);

    const baseColor = isLeft ? kidsColors.blue : kidsColors.pink;

    const base = {
      width: '100%',
      py: { xs: 1.4, sm: 1.8 },
      px: 2,
      borderRadius: '14px',
      fontWeight: 600,
      fontSize: { xs: '0.85rem', sm: '0.95rem' },
      textTransform: 'none',
      border: '2px solid',
      borderColor: `${baseColor}44`,
      color: kidsColors.textPrimary,
      background: `${baseColor}10`,
      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      justifyContent: 'center',
      textAlign: 'center',
      lineHeight: 1.3,
      '&:hover': {
        background: `${baseColor}25`,
        borderColor: baseColor,
        transform: 'scale(1.03)',
      },
    };

    if (matched) {
      return {
        ...base,
        borderColor: matched.color,
        background: `${matched.color}20`,
        opacity: 0.7,
        pointerEvents: 'none',
        boxShadow: `0 0 12px ${matched.color}30`,
      };
    }

    if (isWrong) {
      return {
        ...base,
        borderColor: kidsColors.incorrect,
        background: kidsColors.incorrectBg,
        animation: 'shake 0.4s ease-in-out',
        '@keyframes shake': {
          '0%, 100%': { transform: 'translateX(0)' },
          '20%': { transform: 'translateX(-5px)' },
          '40%': { transform: 'translateX(5px)' },
          '60%': { transform: 'translateX(-3px)' },
          '80%': { transform: 'translateX(3px)' },
        },
      };
    }

    if (isSelected) {
      return {
        ...base,
        borderColor: kidsColors.primary,
        background: `${kidsColors.primary}25`,
        boxShadow: kidsColors.glowPrimary,
        transform: 'scale(1.04)',
      };
    }

    return base;
  };

  // ── render ───────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 640,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        ...kidsAnimations.fadeInUp,
      }}
    >
      {/* Progress */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: kidsColors.surfaceLight,
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: kidsColors.gradientPrimary,
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: kidsColors.textMuted }}>
            Round {currentIndex + 1} / {total}
          </Typography>
          <Typography variant="caption" sx={{ color: kidsColors.primaryLight, fontWeight: 600 }}>
            Matched: {matchedPairs.length} / {pairs.length}
          </Typography>
        </Box>
      </Box>

      {/* Concept tag */}
      {q.concept && (
        <Fade in={visible} timeout={300}>
          <Typography
            variant="overline"
            sx={{
              color: kidsColors.primaryLight,
              letterSpacing: 1.5,
              fontSize: '0.7rem',
              mb: 1.5,
              display: 'block',
              textAlign: 'center',
            }}
          >
            {q.concept}
          </Typography>
        </Fade>
      )}

      {/* Instruction */}
      <Fade in={visible} timeout={350}>
        <Typography
          variant="body2"
          sx={{ color: kidsColors.textSecondary, textAlign: 'center', mb: 2, fontWeight: 500 }}
        >
          Tap one item on each side to match them
        </Typography>
      </Fade>

      {/* Matching area */}
      <Box ref={containerRef} sx={{ position: 'relative', mb: 3 }}>
        {/* SVG overlay for connection lines */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {linePositions.map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={line.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeDasharray="6 4"
              opacity={0.7}
            />
          ))}
        </svg>

        {/* Two columns */}
        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 } }}>
          {/* Left column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pairs.map((pair, idx) => (
              <Grow in={visible} key={`l-${idx}`} timeout={300 + idx * 80}>
                <Button
                  ref={(el) => (leftRefs.current[idx] = el)}
                  variant="outlined"
                  onClick={() => handleLeftClick(idx)}
                  sx={itemSx('left', idx, idx)}
                >
                  {pair.left}
                </Button>
              </Grow>
            ))}
          </Box>

          {/* Right column (shuffled) */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {shuffledRightIndices.map((origIdx, displayIdx) => (
              <Grow in={visible} key={`r-${displayIdx}`} timeout={300 + displayIdx * 80}>
                <Button
                  ref={(el) => (rightRefs.current[displayIdx] = el)}
                  variant="outlined"
                  onClick={() => handleRightClick(displayIdx)}
                  sx={itemSx('right', displayIdx, origIdx)}
                >
                  {pairs[origIdx].right}
                </Button>
              </Grow>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Completion celebration */}
      {matchedPairs.length === pairs.length && (
        <Fade in timeout={400}>
          <Box
            sx={{
              textAlign: 'center',
              animation: 'celebrate 0.8s ease-in-out',
              ...kidsAnimations.celebrate,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: kidsColors.gradientCelebration,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              All matched!
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
